/**
 * The footer's "Updated ..." line (REQ-WEB-044, D55).
 *
 * Pure functions with no imports, so the wording can be checked on its own.
 * The API sends an ABSOLUTE time and counts per rolling window; everything a
 * visitor reads is worked out here, so a cached answer never shows a stale
 * "2 hours ago".
 */

export interface WindowCounts {
  mounts_added: number;
  lenses_added: number;
  lenses_with_new_specs: number;
  values_corrected: number;
}

export interface Freshness {
  updated_at: string | null;
  windows: Record<string, WindowCounts>;
}

/** Shortest first. The line uses the first window worth showing. */
export const WINDOW_ORDER: ReadonlyArray<readonly [string, string]> = [
  ["24h", "Past 24 hours"],
  ["7d", "Past week"],
  ["30d", "Past month"],
  ["365d", "Past year"],
];

/** The bar (REQ-WEB-044): 1 mount, 1 lens added, specs on 10 lenses, or 10 corrections. */
export function worthShowing(c: WindowCounts): boolean {
  return c.mounts_added >= 1 || c.lenses_added >= 1 ||
    c.lenses_with_new_specs >= 10 || c.values_corrected >= 10;
}

const n = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/**
 * The non-zero kinds, most notable first, at most three (kinds, not words).
 * Mounts and lenses share one "added" ("2 mounts and 13 lenses added"); the
 * others stand on their own.
 */
export function describeChanges(c: WindowCounts): string {
  const kinds: Array<[string, string]> = [];
  if (c.mounts_added > 0) kinds.push(["added", n(c.mounts_added, "mount", "mounts")]);
  if (c.lenses_added > 0) kinds.push(["added", n(c.lenses_added, "lens", "lenses")]);
  if (c.lenses_with_new_specs > 0) {
    kinds.push(["own", `specs added to ${n(c.lenses_with_new_specs, "lens", "lenses")}`]);
  }
  if (c.values_corrected > 0) {
    kinds.push(["own", `${n(c.values_corrected, "value", "values")} corrected`]);
  }
  const top = kinds.slice(0, 3);
  const added = top.filter(([t]) => t === "added").map(([, text]) => text);
  const parts = added.length ? [`${added.join(" and ")} added`] : [];
  for (const [t, text] of top) if (t === "own") parts.push(text);
  return parts.join(", ");
}

/** "2 hours ago", rendered from the absolute time against `now`. */
export function relativeTime(iso: string, now: number): string {
  const secs = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  const units: ReadonlyArray<readonly [number, string]> = [
    [365 * 86400, "year"], [30 * 86400, "month"], [86400, "day"], [3600, "hour"], [60, "minute"],
  ];
  for (const [size, name] of units) {
    if (secs >= size) {
      const k = Math.floor(secs / size);
      return `${k} ${name}${k === 1 ? "" : "s"} ago`;
    }
  }
  return "just now";
}

/** The whole line, or null when there is nothing true to say. */
export function freshnessLine(f: Freshness, now: number): string | null {
  if (!f.updated_at || Number.isNaN(Date.parse(f.updated_at))) return null;
  const head = `Updated ${relativeTime(f.updated_at, now)}`;
  for (const [key, label] of WINDOW_ORDER) {
    const c = f.windows?.[key];
    if (c && worthShowing(c)) return `${head} · ${label}: ${describeChanges(c)}`;
  }
  return head;
}
